import type { TournamentState } from '@ps/web-poker-tile-mappers';
import { AttributeKey } from '@ps/web-poker-tile-mappers';
import classNames from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';

import { stripHTML } from '@swf/poker';

import type { HGPokerGameTile } from '../../../types/common';
import Attribute from '../Attribute/Attribute';
import PlayersIcon from '../assets/players.svg';

import { TXTCLI_Enrolled, TXTCLI_Table_Size } from './Footer.messages.json';
import styles from './Footer.scss';
import useGetBottomAttributes from './useGetBottomAttributes';
import useGetTournamentState from './useGetTournamentState';

interface Props {
  attributes: HGPokerGameTile['attributes'];
  tableName?: HGPokerGameTile['name'];
  maxPlayers?: HGPokerGameTile['maxPlayers'];
  canSelect: boolean;
  id: HGPokerGameTile['id'];
  tournamentState?: TournamentState;
  playersSeated?: number;
  entrants?: number;
}

const Footer: React.FC<Props> = ({
  attributes,
  tableName,
  maxPlayers,
  canSelect,
  id,
  tournamentState,
  playersSeated,
  entrants
}) => {
  const gameName = stripHTML(tableName);
  const tournState = useGetTournamentState(attributes, tournamentState);
  const bottomSectionItems = useGetBottomAttributes(attributes);
  const { formatMessage } = useIntl();
  const nameTooltip = entrants !== undefined ? TXTCLI_Enrolled : TXTCLI_Table_Size;
  const players = {
    value:
      playersSeated !== undefined
        ? `${playersSeated}/${maxPlayers}`
        : entrants !== undefined
        ? `${entrants}`
        : `${maxPlayers}`,
    key: AttributeKey.players,
    name: formatMessage(nameTooltip)
  };

  return (
    <div className={styles.container} data-testid="footer">
      <div className={styles.top}>
        {tournState && (
          <Attribute
            attributeKey={tournState.key}
            key={tournState.key}
            name={tournState.name}
            value={`${tournState.name} ${tournState.value}`}
          />
        )}
      </div>
      <div className={styles.middle}>
        <div className={styles.name} title={`${gameName}`}>
          {gameName}
        </div>
        <Attribute
          attributeKey={players.key}
          key={players.key}
          name={players.name}
          value={players.value}
          icon={<PlayersIcon />}
          className={classNames({
            [styles.full]: playersSeated === maxPlayers
          })}
        />
      </div>
      <div className={styles.bottom}>
        {bottomSectionItems.map(({ value, key, name }) => (
          <div key={key} className={styles.attribute}>
            <div className={styles.attributeName}>{name}</div>
            <div className={styles.attributeValue}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Footer;
